import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import TagRoundedIcon from "@mui/icons-material/TagRounded";
import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SlidingPlayMain from "../engine/SlidingPlayMain";
import { slidingPlayGridProps } from "../lib/appearance";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import {
    NATIVE_PURCHASE_PATH,
    SLIDING_MASTER_PRODUCT_SKU,
} from "../_stubs/nativePurchaseConstants";
import PlayPageNav from "../components/PlayPageNav";
import PlaySessionHud from "../components/PlaySessionHud";
import RecordingCaptureStage from "../components/RecordingCaptureStage";
import SlidingPlayConfetti from "../components/SlidingPlayConfetti";
import SlidingVictoryModal from "../components/SlidingVictoryModal";
import SlidingPlayMistBackground from "../../../../pages/puzzles/sliding/SlidingPlayMistBackground";
import AudioButton from "../../../../pages/components/AudioButton";
import IconImagePreview from "../../../../pages/components/IconImagePreview";
import { LOCAL_BGM_DEEP_URBAN } from "../_stubs/localBgm";
import {
    PLAY_TOOLBAR_BTN_SIZE,
    PLAY_TOOLBAR_BTN_SIZE_RECORDING,
    PLAY_TOOLBAR_ICON_SIZE,
    PLAY_TOOLBAR_ICON_SIZE_RECORDING,
    playToolbarActionBtnSx,
} from "../styles/playToolbarStyles";
import {
    buildLocalPlayNavigateState,
    getSnapshotPlayBootstrap,
    getSnapshotPlayDefaults,
    getSnapshotSlidingId,
    isDemoSlidingImageUrl,
    isSnapshotUiTesting,
    notifyNativeSnapshotPlayReady,
    SNAPSHOT_PLAY_READY_LABEL,
} from "../lib/access";
import {
    beginPlayFromSlidingId,
    buildSnapshotPlaySessionFromBootstrap,
    findFirstLibrarySnapshotSession,
    findSnapshotPlaySession,
    findTemplateForQuickPlay,
    normalizePlaySession,
    restartPlay,
    saveProgress,
    startPlay,
} from "../lib/api";
import {
    CONTENT_RECORDING_6X6_HARD_BOARD,
    CONTENT_RECORDING_6X6_HARD_DIFFICULTY,
    CONTENT_RECORDING_6X6_HARD_GRID,
    CONTENT_RECORDING_DIFFICULTY,
    CONTENT_RECORDING_GRID_SIZE,
    CONTENT_RECORDING_GHOST_CHIP_LEAD_MS,
    CONTENT_RECORDING_INTRO_MS,
    CONTENT_RECORDING_MOVE_MS,
    contentRecordingBoard,
    contentRecordingPauseIndexes,
    isSlidingContentRecording,
    loadContentRecordingPuzzle,
    solvePathTapSlices,
} from "../lib/contentRecording";
import {
    clearSavedGame,
    loadBgmAutoPlayPreference,
    loadPlaySoundEffectsPreference,
    loadPlayTileModePreference,
    loadShowPlayTimerPreference,
    resolvePlaySoundEffectsPreference,
    saveBgmAutoPlayPreference,
    saveGame,
    savePlayTileModePreference,
} from "../lib/storage";
import { saveAuthenticatedPlayGridPreferences } from "../lib/playGridPreferences";
import { shuffledLayout, shuffleMoveCount } from "../lib/utils";
import { formatElapsedMmSs, usePlaySessionTimer } from "../lib/usePlaySessionTimer";
import { slidingSafeAreaBottom } from "../styles/mainStyles";
import { usePagesTranslation } from "../../../../pages/locales";
import AuthService from "../../../../services/AuthService";
import UserService from "../../../../services/UserService";
import { readCoinsEarned, readTotalPuzzleCoinsFromPlay } from "../../../../services/PuzzleCoinsUtil";
import { resolveSlidingPuzzleScore } from "../lib/slidingScoreCatalog";
import {
    attachPlayReturnPath,
    hydratePlayRouteState,
    navigateToPlay,
    resolvePlayReturnPath,
    savePlayRouteState,
} from "../lib/playRouteState";
import {
    buildPremiumPlayCreditSessionKey,
    consumeCreditForPremiumPlayStart,
    isInsufficientCreditError,
    shouldChargeCreditsForPremiumPlay,
    wasPremiumPlayCreditCharged,
} from "../lib/slidingCreditConsume";
import HintPointsSheet from "../components/HintPointsSheet";
import GhostGuideHintButton from "../components/GhostGuideHintButton";
import { useGhostGuideAssistant } from "../lib/useGhostGuideAssistant";
import {
    GHOST_GUIDE_HOLD_ALL_MS,
    runGhostGuideSequence,
} from "../engine/slidingGhostGuideEffects";
import { GHOST_GUIDE_STEP_COUNT } from "../engine/slidingGhostGuideSolver";
import {
    GHOST_GUIDE_TEST_MODE,
    GHOST_GUIDE_TEST_SCRAMBLE_MOVES,
} from "../lib/ghostGuideTestConfig";
import { HINT_POINT_COST } from "../lib/hintPointsConstants";
import { useSubscription } from "../lib/SubscriptionContext";
import { pickSlidingOriginalImageUrl } from "../../../../pages/browse-sliding/slidingBrowseUtils";
import { DEMO_IMAGE, MAIN_PATH, PLAY_PATH, SETTINGS_PATH } from "../routes";

const VALID_GRID_SIZES = [3, 4, 6];
const DEFAULT_GRID_SIZE = 4;
/** Delay after full image reveal before opening the victory modal. */
const SOLVED_IMAGE_REVEAL_MS = 3000;
/** Confetti keeps raining through solve reveal and victory modal. */
const VICTORY_CONFETTI_MS = 15000;
/** Landscape vertical insets — board is height-bound; keep chrome clear of the long edges. */
const PLAY_LANDSCAPE_TOP_PAD = "4px";
const PLAY_LANDSCAPE_BOTTOM_PAD = "4px";
const PLAY_LANDSCAPE_LEFT_PAD = "calc(8px + env(safe-area-inset-left, 0px))";
const PLAY_LANDSCAPE_RIGHT_PAD = "calc(8px + env(safe-area-inset-right, 0px))";

const playBoardSlotSx = {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: "100%",
    containerType: "size",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

/** Largest 1:1 square that fits the play area (portrait → width; landscape → height). */
const playBoardSquareSx = {
    aspectRatio: "1 / 1",
    // Floor with vmin: Android WebView can resolve cqw/cqh to 0 in flex containers.
    width: "min(100%, max(40vmin, min(100cqw, 100cqh)))",
    height: "min(100%, max(40vmin, min(100cqw, 100cqh)))",
    maxWidth: "100%",
    maxHeight: "100%",
    flexShrink: 0,
    overflow: "hidden",
    boxSizing: "border-box",
    "@supports not (width: 1cqw)": {
        width: "min(100%, 70vmin)",
        height: "min(100%, 70vmin)",
        maxHeight: "100%",
    },
};

function normalizeGridSize(value) {
    const n = Number(value);
    return VALID_GRID_SIZES.includes(n) ? n : DEFAULT_GRID_SIZE;
}

function normalizeDifficulty(value) {
    if (value === "easy" || value === "medium" || value === "hard") return value;
    return "easy";
}

function isSolvedLayout(layout, gridSize) {
    const n = gridSize * gridSize;
    return (
        Array.isArray(layout) &&
        layout.length === n &&
        layout.every((v, i) => Number(v) === i)
    );
}

function layoutMatchesGrid(layout, gridSize) {
    const gs = normalizeGridSize(gridSize);
    return Array.isArray(layout) && layout.length === gs * gs;
}

export default function PlayPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { t } = usePagesTranslation();
    const { tokens, isDark } = useSlidingAppearance();
    const viewportLandscape = useMediaQuery("(orientation: landscape)");
    /** Android PlayToolbar glass fill. */
    const playGlass = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)";
    const {
        access,
        credits,
        hintPoints,
        purchaseHintPoints,
        isConfigurationLocked,
        notifyAccessRefresh,
        hasLegacyUnlimitedAccess,
    } = useSubscription();
    const state = useMemo(
        () => hydratePlayRouteState(location.state, searchParams),
        [location.state, searchParams],
    );
    const playReturnPath = resolvePlayReturnPath(state, MAIN_PATH);
    const snapshotMode = isSnapshotUiTesting();
    const snapshotSlidingId = getSnapshotSlidingId();
    const snapshotDefaults = getSnapshotPlayDefaults();

    const routeSlidingId =
        (typeof state.slidingId === "string" && state.slidingId.trim()
            ? state.slidingId.trim()
            : null) || snapshotSlidingId;

    const [resolvedSlidingId, setResolvedSlidingId] = useState(null);
    const effectiveSlidingId = routeSlidingId || resolvedSlidingId;
    const contentRecordingMode = isSlidingContentRecording(searchParams, routeSlidingId);
    /**
     * 1080p content: each load uses the opposite of the last saved mode (image ↔ numbers).
     * Persist in an effect so React Strict Mode does not double-flip localStorage.
     */
    const [tileMode, setTileMode] = useState(() => {
        if (isSlidingContentRecording(searchParams, routeSlidingId)) {
            const last = loadPlayTileModePreference();
            return last === "numbers" ? "image" : "numbers";
        }
        return loadPlayTileModePreference() || "image";
    });

    useEffect(() => {
        if (!contentRecordingMode) return undefined;
        savePlayTileModePreference(tileMode);
        return undefined;
    }, [contentRecordingMode, tileMode]);
    const tileModeRef = useRef(tileMode);
    tileModeRef.current = tileMode;
    /** 1080p capture frame is always 16:9 landscape (same as feature-graphic popup). */
    const isLandscape = contentRecordingMode || viewportLandscape;

    const canSyncProgress = Boolean(effectiveSlidingId) && !snapshotMode;

    const snapshotBootSession =
        snapshotMode && routeSlidingId
            ? buildSnapshotPlaySessionFromBootstrap(routeSlidingId, getSnapshotPlayBootstrap())
            : null;

    /** Signed-in + template id → POST start on mount; progress uses {@link canSyncProgress} + live auth check. */
    const syncPlayApi =
        canSyncProgress && !access.loading && access.isAuthenticated;

    const [snapshotSession, setSnapshotSession] = useState(() => {
        if (!snapshotMode) return null;
        const sid = getSnapshotSlidingId();
        if (!sid) return null;
        return buildSnapshotPlaySessionFromBootstrap(sid, getSnapshotPlayBootstrap());
    });
    const [snapshotLoadFailed, setSnapshotLoadFailed] = useState(false);
    const [contentPlayImageUrl, setContentPlayImageUrl] = useState(null);
    const [contentBoardReady, setContentBoardReady] = useState(false);
    const [contentStoryTitle, setContentStoryTitle] = useState(null);
    const contentBoardKey = contentRecordingBoard(searchParams);
    const contentSixBySixHard = contentBoardKey === CONTENT_RECORDING_6X6_HARD_BOARD;
    const [contentGridSize, setContentGridSize] = useState(
        contentSixBySixHard ? CONTENT_RECORDING_6X6_HARD_GRID : CONTENT_RECORDING_GRID_SIZE,
    );
    const [contentDifficulty, setContentDifficulty] = useState(
        contentSixBySixHard ? CONTENT_RECORDING_6X6_HARD_DIFFICULTY : CONTENT_RECORDING_DIFFICULTY,
    );
    const [recordingGhostMarks, setRecordingGhostMarks] = useState({});
    const [recordingGhostActive, setRecordingGhostActive] = useState(false);
    const activeSnapshotSession = snapshotSession || snapshotBootSession;
    const stateGridSize = normalizeGridSize(state.gridSize ?? snapshotDefaults?.gridSize);
    const sessionGridSize = normalizeGridSize(activeSnapshotSession?.gridSize);
    const gridSize = contentRecordingMode
        ? contentGridSize
        : (() => {
            if (layoutMatchesGrid(state.layout, stateGridSize)) return stateGridSize;
            if (
                activeSnapshotSession?.layout &&
                layoutMatchesGrid(activeSnapshotSession.layout, sessionGridSize)
            ) {
                return sessionGridSize;
            }
            return stateGridSize;
        })();
    const difficulty = contentRecordingMode
        ? contentDifficulty
        : normalizeDifficulty(
            state.difficulty ?? activeSnapshotSession?.difficulty ?? snapshotDefaults?.difficulty
        );
    const imageUrl = (() => {
        if (contentRecordingMode && contentPlayImageUrl) return contentPlayImageUrl;
        const fromState =
            typeof state.imageUrl === "string" && state.imageUrl.trim() ? state.imageUrl.trim() : "";
        if (fromState) return fromState;
        const fromSession =
            typeof activeSnapshotSession?.imageUrl === "string" && activeSnapshotSession.imageUrl.trim()
                ? activeSnapshotSession.imageUrl.trim()
                : "";
        if (fromSession) return fromSession;
        if (snapshotMode) return "";
        return DEMO_IMAGE;
    })();

    const hasPlayImage = Boolean(imageUrl) && !isDemoSlidingImageUrl(imageUrl);
    const localBoardReady = snapshotMode && hasPlayImage && layoutMatchesGrid(state.layout, gridSize);
    const snapshotPuzzleLoaded = hasPlayImage && (Boolean(activeSnapshotSession) || localBoardReady);

    const puzzleTitle = (() => {
        if (contentRecordingMode && contentStoryTitle) return contentStoryTitle;
        const fromState = typeof state.storyTitle === "string" ? state.storyTitle.trim() : "";
        if (fromState) return fromState;
        const fromSession =
            typeof activeSnapshotSession?.storyTitle === "string" ? activeSnapshotSession.storyTitle.trim() : "";
        return fromSession;
    })();

    const canResume =
        Boolean(state.resume) &&
        Array.isArray(state.layout) &&
        state.layout.length === gridSize * gridSize;

    /** Resume navigation (home / My Puzzles) — never charge again for the same puzzle session. */
    const isExplicitResume = Boolean(state.resume);

    const bootLayout =
        (snapshotBootSession?.layout && Array.isArray(snapshotBootSession.layout)
            ? snapshotBootSession.layout
            : null) ||
        (snapshotSession?.layout && Array.isArray(snapshotSession.layout) ? snapshotSession.layout : null);

    const [useSavedLayout, setUseSavedLayout] = useState(
        () =>
            canResume ||
            Boolean(bootLayout?.length) ||
            layoutMatchesGrid(state.layout, normalizeGridSize(state.gridSize))
    );
    const [layoutKey, setLayoutKey] = useState(0);
    const [apiReady, setApiReady] = useState(!syncPlayApi);
    const [apiLayout, setApiLayout] = useState(() => {
        if (bootLayout) return bootLayout.map((v) => Number(v));
        const gs = normalizeGridSize(state.gridSize);
        if (Array.isArray(state.layout) && state.layout.length === gs * gs) {
            return state.layout.map((v) => Number(v));
        }
        return null;
    });
    const [apiPreviewImageUrl, setApiPreviewImageUrl] = useState(null);
    const initialLocalBgm = loadBgmAutoPlayPreference();
    const [bgmSettingsLoaded, setBgmSettingsLoaded] = useState(initialLocalBgm !== null);
    const [bgmAutoPlay, setBgmAutoPlay] = useState(() =>
        initialLocalBgm !== null ? initialLocalBgm : true,
    );
    const [playSoundEffects, setPlaySoundEffects] = useState(() =>
        resolvePlaySoundEffectsPreference(true),
    );
    const [showPlayTimer] = useState(() => loadShowPlayTimerPreference());
    const [cloudUserId, setCloudUserId] = useState(null);

    const originalImageUrl = useMemo(() => {
        const candidates = [
            typeof state.previewImageUrl === "string" ? state.previewImageUrl : "",
            activeSnapshotSession?.previewImageUrl,
            apiPreviewImageUrl,
            imageUrl,
        ];
        for (const candidate of candidates) {
            if (typeof candidate === "string" && candidate.trim()) {
                return candidate.trim();
            }
        }
        return null;
    }, [state.previewImageUrl, activeSnapshotSession?.previewImageUrl, apiPreviewImageUrl, imageUrl]);

    const revealImageUrl = imageUrl || originalImageUrl;
    const [successCardOpen, setSuccessCardOpen] = useState(false);
    const [nextSameConfigPuzzle, setNextSameConfigPuzzle] = useState(null);
    const [nextPuzzleStarting, setNextPuzzleStarting] = useState(false);
    const [hintPointsSheetOpen, setHintPointsSheetOpen] = useState(false);
    const [puzzleSolved, setPuzzleSolved] = useState(false);
    const [solveInProgress, setSolveInProgress] = useState(false);
    const [apiSessionCompleted, setApiSessionCompleted] = useState(false);
    /** Backend rejects PUT /progress on completed plays — skip sync for those sessions. */
    const progressSyncBlockedRef = useRef(false);
    /** Only true after POST /start returns a non-completed session (avoids 400 races while auth loads). */
    const progressSyncAllowedRef = useRef(false);
    const boardRef = useRef(null);
    const [previewBoardSize, setPreviewBoardSize] = useState(null);
    const [boardOverlayRect, setBoardOverlayRect] = useState(null);

    const getBoardOverlayRect = useCallback(() => {
        const el = boardRef.current;
        if (!el) {
            return null;
        }
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return null;
        }
        return {
            top: Math.round(rect.top),
            left: Math.round(rect.left),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
        };
    }, []);

    useLayoutEffect(() => {
        const el = boardRef.current;
        if (!el) {
            return undefined;
        }
        const measure = () => {
            const rect = getBoardOverlayRect();
            if (rect) {
                setBoardOverlayRect(rect);
                setPreviewBoardSize(rect.width);
            }
        };
        measure();
        if (typeof ResizeObserver === "undefined") {
            return undefined;
        }
        const observer = new ResizeObserver(() => {
            measure();
        });
        observer.observe(el);
        const onViewportChange = () => {
            measure();
        };
        window.addEventListener("resize", onViewportChange);
        window.addEventListener("scroll", onViewportChange, true);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", onViewportChange);
            window.removeEventListener("scroll", onViewportChange, true);
        };
    }, [puzzleSolved, gridSize, layoutKey, apiReady, getBoardOverlayRect]);
    const [successCardStats, setSuccessCardStats] = useState({
        time: "0:00",
        moves: 0,
        score: null,
        totalScore: null,
    });
    const progressTimer = useRef(null);
    const successCardShownRef = useRef(false);
    const consumedCreditSessionKeyRef = useRef(null);
    const creditConsumeInFlightRef = useRef(null);
    const playSessionEnteredRef = useRef(false);
    const successCardTimerRef = useRef(null);
    const confettiTimerRef = useRef(null);
    const [confettiActive, setConfettiActive] = useState(false);
    const [confettiBurstKey, setConfettiBurstKey] = useState(0);
    const moveCountRef = useRef(0);
    const [displayMoves, setDisplayMoves] = useState(0);
    const timerSessionKey = `${effectiveSlidingId || "local"}-${gridSize}-${difficulty}-${layoutKey}`;
    const playTimer = usePlaySessionTimer({
        active: apiReady && !puzzleSolved && !solveInProgress,
        resetKey: timerSessionKey,
    });
    const { pause: pausePlayTimer, getElapsedMs: getPlayElapsedMs, reset: resetPlayTimer, resumeFromUserMove } =
        playTimer;

    useEffect(() => {
        if (routeSlidingId || snapshotMode) return undefined;
        let cancelled = false;
        (async () => {
            const authed = await AuthService.isAuthenticated();
            if (!authed || cancelled) return;
            const found = await findTemplateForQuickPlay(gridSize, difficulty);
            if (cancelled || !found?.slidingId) {
                console.warn("[SlidingPlay] no slidingId on play page — progress API disabled");
                return;
            }
            setResolvedSlidingId(found.slidingId);
            savePlayRouteState({ ...state, slidingId: found.slidingId, gridSize, difficulty });
        })();
        return () => {
            cancelled = true;
        };
    }, [routeSlidingId, snapshotMode, gridSize, difficulty, state]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const userInfo = await UserService.getUserInfo().catch(() => null);
            if (cancelled) return;
            setCloudUserId(userInfo?.cloudUser?.id ?? null);
            if (loadBgmAutoPlayPreference() === null) {
                const settings = UserService.getSettingsFormData(userInfo);
                setBgmAutoPlay(settings.bgmAutoPlay);
            }
            if (loadPlaySoundEffectsPreference() === null) {
                const settings = UserService.getSettingsFormData(userInfo);
                setPlaySoundEffects(settings.soundEnabled !== false);
            }
            setBgmSettingsLoaded(true);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleBgmPlayingChange = useCallback((playing) => {
        setBgmAutoPlay(playing);
        saveBgmAutoPlayPreference(playing);
    }, []);

    useEffect(() => {
        if (snapshotMode || access.freeTier || access.loading) return;
        void saveAuthenticatedPlayGridPreferences({ gridSize, difficulty });
    }, [gridSize, difficulty, snapshotMode, access.freeTier, access.loading]);

    useEffect(() => {
        if (access.loading || snapshotMode || contentRecordingMode) return;
        if (playSessionEnteredRef.current) return;
        // Wait for POST /start so deep links (?slidingId=) resolve real grid/difficulty first.
        if (syncPlayApi && !apiReady) return;
        if (isConfigurationLocked(gridSize, difficulty)) {
            navigate(MAIN_PATH, { replace: true });
        }
    }, [
        access.loading,
        snapshotMode,
        contentRecordingMode,
        gridSize,
        difficulty,
        navigate,
        isConfigurationLocked,
        syncPlayApi,
        apiReady,
    ]);

    useEffect(() => {
        if (snapshotMode) return undefined;
        if (apiReady) {
            playSessionEnteredRef.current = true;
        }
        return undefined;
    }, [apiReady, snapshotMode]);

    useEffect(() => {
        return () => {
            if (confettiTimerRef.current) {
                window.clearTimeout(confettiTimerRef.current);
                confettiTimerRef.current = null;
            }
        };
    }, []);

    const startVictoryConfetti = useCallback(() => {
        setConfettiBurstKey((key) => key + 1);
        setConfettiActive(true);
        if (confettiTimerRef.current) {
            window.clearTimeout(confettiTimerRef.current);
        }
        confettiTimerRef.current = window.setTimeout(() => {
            confettiTimerRef.current = null;
            setConfettiActive(false);
        }, VICTORY_CONFETTI_MS);
    }, []);

    const stopVictoryConfetti = useCallback(() => {
        if (confettiTimerRef.current) {
            window.clearTimeout(confettiTimerRef.current);
            confettiTimerRef.current = null;
        }
        setConfettiActive(false);
    }, []);

    const showVictorySignUp =
        !access.loading && access.freeTier && !access.isAuthenticated;

    useEffect(() => {
        if (!syncPlayApi) {
            // Guest or auth still loading — never PUT /progress until start confirms an open session.
            progressSyncAllowedRef.current = false;
            setApiReady(true);
            return undefined;
        }
        setApiReady(false);
        progressSyncAllowedRef.current = false;
        progressSyncBlockedRef.current = false;
        let cancelled = false;
        (async () => {
            const session = await startPlay(effectiveSlidingId);
            if (cancelled) return;
            if (!session) {
                // Start failed — do not risk PUT /progress (completed plays 400).
                progressSyncBlockedRef.current = true;
                progressSyncAllowedRef.current = false;
                setApiReady(true);
                return;
            }
            if (session.isCompleted) {
                // Server keeps the completed row; PUT /progress would 400.
                progressSyncBlockedRef.current = true;
                progressSyncAllowedRef.current = false;
                if (contentRecordingMode) {
                    setApiSessionCompleted(false);
                } else if (isExplicitResume || canResume) {
                    setApiSessionCompleted(true);
                    if (
                        session.layout &&
                        layoutMatchesGrid(session.layout, session.gridSize ?? gridSize)
                    ) {
                        setApiLayout(session.layout);
                        setUseSavedLayout(true);
                    }
                } else {
                    // Local replay only — do not call progress API.
                    setApiSessionCompleted(false);
                    setUseSavedLayout(false);
                    setApiLayout(null);
                }
            } else {
                progressSyncAllowedRef.current = contentRecordingMode ? false : true;
                if (
                    !contentRecordingMode &&
                    session.layout &&
                    layoutMatchesGrid(session.layout, session.gridSize ?? gridSize)
                ) {
                    setApiLayout(session.layout);
                    setUseSavedLayout(true);
                }
                setApiSessionCompleted(false);
            }
            if (session.previewImageUrl) {
                setApiPreviewImageUrl(session.previewImageUrl);
            }
            if (contentRecordingMode) {
                progressSyncAllowedRef.current = false;
                progressSyncBlockedRef.current = true;
                setApiSessionCompleted(false);
            }
            setApiReady(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [syncPlayApi, effectiveSlidingId, canResume, isExplicitResume, gridSize, contentRecordingMode]);

    /** Snapshot play without `-PuzzleSnapshotSlidingId`: only when navigation omitted template id. */
    useEffect(() => {
        if (!snapshotMode || routeSlidingId || resolvedSlidingId) return undefined;
        if (state.imageUrl && layoutMatchesGrid(state.layout, gridSize)) return undefined;
        let cancelled = false;
        setSnapshotLoadFailed(false);
        (async () => {
            const session = await findFirstLibrarySnapshotSession();
            if (cancelled) return;
            if (session?.slidingId) {
                setResolvedSlidingId(session.slidingId);
                return;
            }
            setSnapshotLoadFailed(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [snapshotMode, routeSlidingId, resolvedSlidingId, state.imageUrl, state.layout, gridSize]);

    /** Snapshot: native bootstrap first, then browse API (no JWT startPlay). */
    useEffect(() => {
        if (!snapshotMode || !effectiveSlidingId) return undefined;
        if (state.imageUrl && layoutMatchesGrid(state.layout, gridSize) && !getSnapshotPlayBootstrap()) {
            return undefined;
        }
        let cancelled = false;
        setSnapshotLoadFailed(false);

        const bootstrap = getSnapshotPlayBootstrap();
        const bootSession = buildSnapshotPlaySessionFromBootstrap(effectiveSlidingId, bootstrap);
        if (bootSession) {
            setSnapshotSession(bootSession);
            if (layoutMatchesGrid(bootSession.layout, bootSession.gridSize ?? gridSize)) {
                setApiLayout(bootSession.layout);
                setUseSavedLayout(true);
            }
        }

        (async () => {
            const session = bootSession || (await findSnapshotPlaySession(effectiveSlidingId));
            if (cancelled) return;
            if (!session || isDemoSlidingImageUrl(session.imageUrl)) {
                if (!bootSession) setSnapshotLoadFailed(true);
                return;
            }
            setSnapshotSession(session);
            const sessionGs = normalizeGridSize(session.gridSize ?? gridSize);
            if (layoutMatchesGrid(session.layout, sessionGs)) {
                setApiLayout(session.layout);
                setUseSavedLayout(true);
            }
            if (session.isCompleted) {
                setApiSessionCompleted(true);
            }
            if (session.previewImageUrl) {
                setApiPreviewImageUrl(session.previewImageUrl);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [snapshotMode, effectiveSlidingId, state.imageUrl, state.layout, gridSize]);

    const startingLayout = useMemo(() => {
        const n = gridSize * gridSize;
        if (contentRecordingMode) {
            if (apiLayout && apiLayout.length === n) {
                return apiLayout.map((v) => Number(v));
            }
            return [];
        }
        if (
            GHOST_GUIDE_TEST_MODE &&
            !isExplicitResume &&
            !canResume &&
            !(snapshotMode && apiLayout && useSavedLayout)
        ) {
            return shuffledLayout(gridSize, GHOST_GUIDE_TEST_SCRAMBLE_MOVES);
        }
        if (apiLayout && useSavedLayout && (syncPlayApi || snapshotMode)) {
            const mapped = apiLayout.map((v) => Number(v));
            if (mapped.length === n) return mapped;
        }
        if (useSavedLayout && canResume) {
            return state.layout.map((v) => Number(v));
        }
        return shuffledLayout(gridSize, shuffleMoveCount(difficulty));
    }, [
        syncPlayApi,
        snapshotMode,
        contentRecordingMode,
        apiLayout,
        useSavedLayout,
        canResume,
        isExplicitResume,
        state.layout,
        gridSize,
        difficulty,
        layoutKey,
    ]);

    const boardLayout = startingLayout;

    const contentAutoSolveStartedRef = useRef(false);
    const contentSolveLayoutsRef = useRef([]);
    const contentGhostGuidesRef = useRef(0);
    const [contentAutoSolveBusy, setContentAutoSolveBusy] = useState(false);

    useEffect(() => {
        if (!contentRecordingMode) {
            setContentBoardReady(true);
            contentAutoSolveStartedRef.current = false;
            contentSolveLayoutsRef.current = [];
            contentGhostGuidesRef.current = 0;
            setContentAutoSolveBusy(false);
            setRecordingGhostMarks({});
            setRecordingGhostActive(false);
            return undefined;
        }
        let cancelled = false;
        setContentBoardReady(false);
        contentAutoSolveStartedRef.current = false;
        contentSolveLayoutsRef.current = [];
        contentGhostGuidesRef.current = 0;
        setContentAutoSolveBusy(false);
        setRecordingGhostMarks({});
        setRecordingGhostActive(false);
        (async () => {
            const loaded = await loadContentRecordingPuzzle({ board: contentBoardKey });
            if (cancelled || !loaded) return;
            setApiLayout(loaded.layout);
            contentSolveLayoutsRef.current = Array.isArray(loaded.solveLayouts)
                ? loaded.solveLayouts
                : [];
            contentGhostGuidesRef.current = Number(loaded.ghostGuideCount) || 0;
            setUseSavedLayout(true);
            setApiSessionCompleted(false);
            setContentGridSize(normalizeGridSize(loaded.gridSize));
            setContentDifficulty(normalizeDifficulty(loaded.difficulty));
            if (typeof loaded.imageUrl === "string" && loaded.imageUrl.trim()) {
                setContentPlayImageUrl(loaded.imageUrl.trim());
            }
            if (typeof loaded.previewImageUrl === "string" && loaded.previewImageUrl.trim()) {
                setApiPreviewImageUrl(loaded.previewImageUrl.trim());
            }
            if (typeof loaded.storyTitle === "string" && loaded.storyTitle.trim()) {
                setContentStoryTitle(loaded.storyTitle.trim());
            }
            progressSyncAllowedRef.current = false;
            progressSyncBlockedRef.current = true;
            setContentBoardReady(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [contentRecordingMode, contentBoardKey, effectiveSlidingId]);

    const sessionStartsSolved = useMemo(
        () =>
            contentRecordingMode
                ? false
                : isSolvedLayout(startingLayout, gridSize) || apiSessionCompleted,
        [contentRecordingMode, startingLayout, gridSize, apiSessionCompleted]
    );

    useLayoutEffect(() => {
        if (apiReady && sessionStartsSolved) {
            setPuzzleSolved(true);
            pausePlayTimer();
        }
    }, [apiReady, sessionStartsSolved, pausePlayTimer]);

    /** Premium play: charge 1 credit when entering (not on completion). Skip resume and already-solved sessions. */
    useEffect(() => {
        if (snapshotMode || access.loading || credits.balanceLoading || sessionStartsSolved || isExplicitResume) {
            return undefined;
        }
        if (contentRecordingMode) {
            return undefined;
        }
        if (syncPlayApi && !apiReady) {
            return undefined;
        }

        const sessionKey = buildPremiumPlayCreditSessionKey(effectiveSlidingId, gridSize, difficulty);
        if (
            consumedCreditSessionKeyRef.current === sessionKey ||
            wasPremiumPlayCreditCharged(sessionKey)
        ) {
            return undefined;
        }
        if (creditConsumeInFlightRef.current === sessionKey) {
            return undefined;
        }

        if (
            !shouldChargeCreditsForPremiumPlay({
                gridSize,
                difficulty,
                hasLegacyUnlimitedAccess,
                hasCredits: credits.hasCredits,
            })
        ) {
            return undefined;
        }

        if (!effectiveSlidingId) {
            return undefined;
        }

        creditConsumeInFlightRef.current = sessionKey;
        void (async () => {
            const authed = await AuthService.isAuthenticated();
            if (!authed) {
                if (creditConsumeInFlightRef.current === sessionKey) {
                    creditConsumeInFlightRef.current = null;
                }
                return;
            }

            try {
                const result = await consumeCreditForPremiumPlayStart({
                    slidingId: effectiveSlidingId,
                    gridSize,
                    difficulty,
                });
                if (!result) {
                    return;
                }
                consumedCreditSessionKeyRef.current = sessionKey;
                if (Number.isFinite(Number(result.creditBalance))) {
                    credits.applyCreditBalance(result.creditBalance);
                }
            } catch (e) {
                if (isInsufficientCreditError(e)) {
                    navigate(NATIVE_PURCHASE_PATH, { replace: true });
                }
            } finally {
                if (creditConsumeInFlightRef.current === sessionKey) {
                    creditConsumeInFlightRef.current = null;
                }
            }
        })();
    }, [
        snapshotMode,
        access.loading,
        credits.balanceLoading,
        sessionStartsSolved,
        syncPlayApi,
        apiReady,
        effectiveSlidingId,
        gridSize,
        difficulty,
        hasLegacyUnlimitedAccess,
        credits.hasCredits,
        credits.applyCreditBalance,
        navigate,
        notifyAccessRefresh,
        isExplicitResume,
    ]);

    useEffect(() => {
        setApiPreviewImageUrl(null);
    }, [effectiveSlidingId, gridSize, difficulty]);

    useEffect(() => {
        playSessionEnteredRef.current = false;
    }, [effectiveSlidingId, gridSize, difficulty]);

    useEffect(() => {
        successCardShownRef.current = false;
        moveCountRef.current = 0;
        setDisplayMoves(0);
        setSuccessCardOpen(false);
        setPuzzleSolved(false);
        setSolveInProgress(false);
        setApiSessionCompleted(false);
        setNextSameConfigPuzzle(null);
        setNextPuzzleStarting(false);
        if (successCardTimerRef.current) {
            window.clearTimeout(successCardTimerRef.current);
            successCardTimerRef.current = null;
        }
        stopVictoryConfetti();
    }, [effectiveSlidingId, gridSize, difficulty, layoutKey, stopVictoryConfetti]);

    useLayoutEffect(() => {
        if (!snapshotMode || !snapshotPuzzleLoaded || !imageUrl) return undefined;
        notifyNativeSnapshotPlayReady();
        const timer = window.setTimeout(() => notifyNativeSnapshotPlayReady(), 500);
        return () => window.clearTimeout(timer);
    }, [snapshotMode, snapshotPuzzleLoaded, imageUrl, layoutKey]);

    const slidingIdRef = useRef(effectiveSlidingId);
    slidingIdRef.current = effectiveSlidingId;

    const resolveSlidingIdForProgress = useCallback(() => {
        const fromRef = slidingIdRef.current;
        if (fromRef) return fromRef;
        const fromQuery = (searchParams.get("slidingId") || "").trim();
        if (fromQuery) return fromQuery;
        const fromState = typeof state.slidingId === "string" ? state.slidingId.trim() : "";
        return fromState || null;
    }, [searchParams, state.slidingId]);

    const persistToApi = useCallback(
        (layout, completed) => {
            if (
                snapshotMode ||
                contentRecordingMode ||
                !progressSyncAllowedRef.current ||
                progressSyncBlockedRef.current ||
                apiSessionCompleted
            ) {
                return;
            }
            const sid = resolveSlidingIdForProgress();
            if (!sid || !Array.isArray(layout)) {
                console.warn("[SlidingPlay] no slidingId — progress PUT skipped", {
                    demoBoard: imageUrl.includes("sliding-logo-board"),
                });
                return;
            }
            if (progressTimer.current) clearTimeout(progressTimer.current);
            progressTimer.current = setTimeout(() => {
                progressTimer.current = null;
                if (!progressSyncAllowedRef.current || progressSyncBlockedRef.current) return;
                const currentLayout = layout.map((v) => Number(v));
                void saveProgress(sid, {
                    currentLayout,
                    isCompleted: completed,
                    tileMode: tileModeRef.current,
                }).then((data) => {
                    if (data?.alreadyCompleted) {
                        progressSyncAllowedRef.current = false;
                        progressSyncBlockedRef.current = true;
                        setApiSessionCompleted(true);
                        return;
                    }
                    if (data == null) return;
                });
            }, 400);
        },
        [snapshotMode, contentRecordingMode, resolveSlidingIdForProgress, imageUrl, apiSessionCompleted]
    );

    const handleMove = useCallback(() => {
        resumeFromUserMove();
        moveCountRef.current += 1;
        setDisplayMoves(moveCountRef.current);
    }, [resumeFromUserMove]);
    const handleMoveRef = useRef(handleMove);
    handleMoveRef.current = handleMove;

    /** 1080p content capture — auto-solve the published scramble for OBS. */
    useEffect(() => {
        if (!contentRecordingMode || !contentBoardReady) return undefined;
        if (contentAutoSolveStartedRef.current) return undefined;
        if (!Array.isArray(apiLayout) || apiLayout.length !== gridSize * gridSize) {
            return undefined;
        }
        if (isSolvedLayout(apiLayout, gridSize)) return undefined;

        const startLayout = apiLayout.map((v) => Number(v));
        contentAutoSolveStartedRef.current = true;
        let cancelled = false;
        let timerId = null;
        const wait = (ms) =>
            new Promise((resolve) => {
                timerId = window.setTimeout(() => {
                    timerId = null;
                    resolve();
                }, ms);
            });

        setContentAutoSolveBusy(true);
        const ghostControl = { aborted: false };
        (async () => {
            await wait(CONTENT_RECORDING_INTRO_MS);
            if (cancelled) return;

            const path = contentSolveLayoutsRef.current;
            if (!Array.isArray(path) || !path.length) {
                console.warn("[SlidingPlay] content auto-solve: no path found", {
                    startLen: startLayout.length,
                });
                if (!cancelled) setContentAutoSolveBusy(false);
                return;
            }

            const pauseAt = new Set(
                contentRecordingPauseIndexes(path.length, contentGhostGuidesRef.current),
            );

            const showGhostGuide = async (fromIndex) => {
                const taps = solvePathTapSlices(
                    path,
                    fromIndex,
                    GHOST_GUIDE_STEP_COUNT,
                    gridSize,
                );
                if (!taps.length || cancelled || ghostControl.aborted) return;
                setRecordingGhostMarks({});
                setRecordingGhostActive(true);
                await wait(CONTENT_RECORDING_GHOST_CHIP_LEAD_MS);
                if (cancelled || ghostControl.aborted) return;
                await runGhostGuideSequence(
                    taps,
                    {
                        onHighlight: (sliceIndex, stepOrder) => {
                            if (cancelled || ghostControl.aborted) return;
                            if (sliceIndex == null) {
                                setRecordingGhostMarks({});
                                return;
                            }
                            setRecordingGhostMarks({ [String(sliceIndex)]: stepOrder });
                        },
                    },
                    ghostControl,
                );
                if (cancelled || ghostControl.aborted) return;
                const held = {};
                taps.forEach((slice, idx) => {
                    const key = String(slice);
                    const order = String(idx + 1);
                    held[key] = held[key] ? `${held[key]},${order}` : order;
                });
                setRecordingGhostMarks(held);
                await wait(GHOST_GUIDE_HOLD_ALL_MS);
                if (cancelled || ghostControl.aborted) return;
                setRecordingGhostMarks({});
                setRecordingGhostActive(false);
            };

            for (let i = 0; i < path.length; i += 1) {
                if (cancelled) return;
                setApiLayout(path[i]);
                handleMoveRef.current();
                if (pauseAt.has(i)) {
                    await wait(CONTENT_RECORDING_MOVE_MS);
                    if (cancelled) return;
                    await showGhostGuide(i);
                }
                if (i < path.length - 1) {
                    await wait(CONTENT_RECORDING_MOVE_MS);
                }
            }
            if (!cancelled) {
                setRecordingGhostMarks({});
                setRecordingGhostActive(false);
                setContentAutoSolveBusy(false);
            }
        })();

        return () => {
            cancelled = true;
            ghostControl.aborted = true;
            contentAutoSolveStartedRef.current = false;
            if (timerId != null) window.clearTimeout(timerId);
            setRecordingGhostMarks({});
            setRecordingGhostActive(false);
            setContentAutoSolveBusy(false);
        };
    // apiLayout snapshotted when board first becomes ready — omit so mid-solve updates don't cancel.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startLayout captured once
    }, [contentRecordingMode, contentBoardReady, gridSize]);

    const handleSolveRevealComplete = useCallback(() => {
        setPuzzleSolved(true);
        if (contentRecordingMode) {
            startVictoryConfetti();
            return;
        }
        if (successCardTimerRef.current) {
            window.clearTimeout(successCardTimerRef.current);
        }
        successCardTimerRef.current = window.setTimeout(() => {
            successCardTimerRef.current = null;
            setSuccessCardOpen(true);
            startVictoryConfetti();
        }, SOLVED_IMAGE_REVEAL_MS);
    }, [contentRecordingMode, startVictoryConfetti]);

    const persistLocal = useCallback(
        (layout) => {
            if (snapshotMode || access.freeTier) return;
            if (!Array.isArray(layout) || layout.length !== gridSize * gridSize) return;
            if (isSolvedLayout(layout, gridSize)) {
                clearSavedGame();
                return;
            }
            saveGame({
                gridSize,
                difficulty,
                layout: layout.map((v) => Number(v)),
                imageUrl,
            });
        },
        [gridSize, difficulty, imageUrl, access.freeTier, snapshotMode]
    );

    const liveLayoutRef = useRef(null);

    const ghostGuide = useGhostGuideAssistant({
        gridSize,
        getLayout: () => liveLayoutRef.current,
        pointBalance: hintPoints.pointBalance,
        testMode: GHOST_GUIDE_TEST_MODE,
        enabled:
            !snapshotMode &&
            apiReady &&
            !puzzleSolved &&
            !solveInProgress &&
            !sessionStartsSolved &&
            !contentRecordingMode,
        spendPoints: hintPoints.spendPoints,
        onPointBalanceChange: hintPoints.applyPointBalance,
        onInsufficientPoints: () => setHintPointsSheetOpen(true),
    });

    const handleLayoutChange = useCallback(
        (layout) => {
            if (!Array.isArray(layout) || layout.length !== gridSize * gridSize) return;
            liveLayoutRef.current = layout.map((v) => Number(v));
            const sid = resolveSlidingIdForProgress();
            const solved = isSolvedLayout(layout, gridSize);
            if (solved) {
                if (contentRecordingMode && !contentBoardReady) return;
                if (!successCardShownRef.current && !sessionStartsSolved) {
                    successCardShownRef.current = true;
                    setSolveInProgress(true);
                    pausePlayTimer();
                    if (contentRecordingMode) {
                        setPuzzleSolved(true);
                    } else {
                        setSuccessCardStats({
                            time: formatElapsedMmSs(getPlayElapsedMs()),
                            moves: moveCountRef.current,
                            score: resolveSlidingPuzzleScore(gridSize, difficulty),
                            totalScore: null,
                        });
                    }
                    startVictoryConfetti();
                }
            }
            if (contentRecordingMode) return;
            if (sid && !snapshotMode) {
                if (solved) {
                    if (
                        sessionStartsSolved ||
                        apiSessionCompleted ||
                        !progressSyncAllowedRef.current ||
                        progressSyncBlockedRef.current
                    ) {
                        return;
                    }
                    if (progressTimer.current) {
                        clearTimeout(progressTimer.current);
                        progressTimer.current = null;
                    }
                    const currentLayout = layout.map((v) => Number(v));
                    void (async () => {
                        const authed = await AuthService.isAuthenticated();
                        if (
                            !authed ||
                            !progressSyncAllowedRef.current ||
                            progressSyncBlockedRef.current
                        ) {
                            return;
                        }
                        const data = await saveProgress(sid, {
                            currentLayout,
                            isCompleted: true,
                            tileMode: tileModeRef.current,
                        });
                        if (data?.alreadyCompleted) {
                            progressSyncAllowedRef.current = false;
                            progressSyncBlockedRef.current = true;
                            setApiSessionCompleted(true);
                            return;
                        }
                        if (data) {
                            progressSyncAllowedRef.current = false;
                            progressSyncBlockedRef.current = true;
                            const earned = readCoinsEarned(data);
                            const total = readTotalPuzzleCoinsFromPlay(data);
                            setSuccessCardStats((prev) => ({
                                ...prev,
                                score: earned,
                                totalScore: total > 0 ? total : prev.totalScore,
                            }));
                        }
                    })();
                } else if (
                    progressSyncAllowedRef.current &&
                    !apiSessionCompleted &&
                    !progressSyncBlockedRef.current
                ) {
                    persistToApi(layout, false);
                }
            } else {
                persistLocal(layout);
            }
        },
        [
            gridSize,
            difficulty,
            snapshotMode,
            resolveSlidingIdForProgress,
            persistToApi,
            persistLocal,
            contentBoardReady,
            sessionStartsSolved,
            apiSessionCompleted,
            contentRecordingMode,
            startVictoryConfetti,
            pausePlayTimer,
            getPlayElapsedMs,
        ]
    );

    const handleLayoutChangeRef = useRef(handleLayoutChange);
    handleLayoutChangeRef.current = handleLayoutChange;

    useEffect(() => {
        if (!apiReady) return undefined;
        if (contentRecordingMode && !contentBoardReady) return undefined;
        handleLayoutChangeRef.current(startingLayout);
    }, [startingLayout, apiReady, contentRecordingMode, contentBoardReady]);

    useEffect(() => {
        if (Array.isArray(startingLayout) && startingLayout.length === gridSize * gridSize) {
            liveLayoutRef.current = startingLayout.map((v) => Number(v));
        }
    }, [startingLayout, gridSize]);

    useEffect(
        () => () => {
            if (progressTimer.current) clearTimeout(progressTimer.current);
            if (successCardTimerRef.current) window.clearTimeout(successCardTimerRef.current);
        },
        []
    );

    const reshuffle = useCallback(async () => {
        successCardShownRef.current = false;
        setSuccessCardOpen(false);
        stopVictoryConfetti();
        setPuzzleSolved(false);
        setSolveInProgress(false);
        setApiSessionCompleted(false);
        if (successCardTimerRef.current) {
            window.clearTimeout(successCardTimerRef.current);
            successCardTimerRef.current = null;
        }
        moveCountRef.current = 0;
        setDisplayMoves(0);
        resetPlayTimer();
        if (syncPlayApi && effectiveSlidingId) {
            const session = await restartPlay(effectiveSlidingId);
            const next = normalizePlaySession(session);
            if (next?.layout) {
                if (next.isCompleted) {
                    progressSyncAllowedRef.current = false;
                    progressSyncBlockedRef.current = true;
                } else {
                    progressSyncAllowedRef.current = true;
                    progressSyncBlockedRef.current = false;
                }
                setApiLayout(next.layout);
                setUseSavedLayout(true);
                setLayoutKey((k) => k + 1);
                return;
            }
            // restart failed (e.g. already completed) — local reshuffle only
            progressSyncAllowedRef.current = false;
            progressSyncBlockedRef.current = true;
        }
        setUseSavedLayout(false);
        setLayoutKey((k) => k + 1);
    }, [syncPlayApi, effectiveSlidingId, resetPlayTimer, stopVictoryConfetti]);

    const handleVictoryPlayAgain = useCallback(() => {
        void reshuffle();
    }, [reshuffle]);

    useEffect(() => {
        if (!puzzleSolved || snapshotMode || contentRecordingMode) {
            setNextSameConfigPuzzle(null);
            return undefined;
        }
        let cancelled = false;
        const excludeSlidingId =
            (typeof effectiveSlidingId === "string" && effectiveSlidingId.trim()) ||
            resolveSlidingIdForProgress() ||
            "";
        void findTemplateForQuickPlay(gridSize, difficulty, { excludeSlidingId }).then((found) => {
            if (!cancelled) {
                setNextSameConfigPuzzle(found?.slidingId ? found : null);
            }
        }).catch(() => {
            if (!cancelled) {
                setNextSameConfigPuzzle(null);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [
        puzzleSolved,
        snapshotMode,
        contentRecordingMode,
        gridSize,
        difficulty,
        effectiveSlidingId,
        resolveSlidingIdForProgress,
    ]);

    const handleVictoryNextPuzzle = useCallback(async () => {
        const found = nextSameConfigPuzzle;
        if (!found?.slidingId || nextPuzzleStarting) return;
        if (isConfigurationLocked(found.gridSize ?? gridSize, found.difficulty ?? difficulty)) {
            setSuccessCardOpen(false);
            navigate(NATIVE_PURCHASE_PATH, {
                state: { productSku: SLIDING_MASTER_PRODUCT_SKU },
            });
            return;
        }
        setNextPuzzleStarting(true);
        try {
            void saveAuthenticatedPlayGridPreferences({
                gridSize: found.gridSize ?? gridSize,
                difficulty: found.difficulty ?? difficulty,
            });
            const previewImageUrl = found.story
                ? pickSlidingOriginalImageUrl(found.story)
                : null;
            const returnPath = playReturnPath;
            const authed = await AuthService.isAuthenticated();
            if (!authed) {
                setSuccessCardOpen(false);
                navigateToPlay(
                    navigate,
                    PLAY_PATH,
                    attachPlayReturnPath(
                        buildLocalPlayNavigateState({
                            slidingId: found.slidingId,
                            gridSize: found.gridSize ?? gridSize,
                            difficulty: found.difficulty ?? difficulty,
                            story: found.story,
                            storyTitle: found.storyTitle,
                            imageUrl: found.imageUrl,
                            previewImageUrl,
                        }),
                        returnPath,
                    ),
                );
                return;
            }
            const result = await beginPlayFromSlidingId(found.slidingId, {
                imageUrl: found.imageUrl,
                gridSize: found.gridSize ?? gridSize,
                difficulty: found.difficulty ?? difficulty,
                story: found.story,
                storyTitle: found.storyTitle,
                previewImageUrl,
                returnPath,
            });
            if (result.error || !result.state?.slidingId) {
                return;
            }
            setSuccessCardOpen(false);
            navigateToPlay(
                navigate,
                PLAY_PATH,
                attachPlayReturnPath(result.state, returnPath),
            );
        } finally {
            setNextPuzzleStarting(false);
        }
    }, [
        nextSameConfigPuzzle,
        nextPuzzleStarting,
        isConfigurationLocked,
        gridSize,
        difficulty,
        navigate,
        playReturnPath,
    ]);

    const displayTitle =
        puzzleTitle ||
        t("native.home.featuredPuzzleFallback", { defaultValue: "The Morning Confluence" });

    const showPlayHint = !puzzleSolved && !sessionStartsSolved;
    /** Keep image/numbers control visible for 1080p capture even after auto-solve. */
    const showTileModeToggle =
        contentRecordingMode || (!puzzleSolved && !sessionStartsSolved);
    const toolbarBtnSize = contentRecordingMode
        ? PLAY_TOOLBAR_BTN_SIZE_RECORDING
        : PLAY_TOOLBAR_BTN_SIZE;
    const toolbarIconSize = contentRecordingMode
        ? PLAY_TOOLBAR_ICON_SIZE_RECORDING
        : PLAY_TOOLBAR_ICON_SIZE;
    const toolBtnSx = playToolbarActionBtnSx(tokens, {
        glass: playGlass,
        size: toolbarBtnSize,
    });
    const toolIconSx = { fontSize: toolbarIconSize };

    if (
        !snapshotMode &&
        ((syncPlayApi && !apiReady) ||
            (canSyncProgress && access.loading) ||
            (contentRecordingMode && !contentBoardReady))
    ) {
        const loadingBody = (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: contentRecordingMode ? "100%" : "50dvh",
                    width: "100%",
                    height: contentRecordingMode ? "100%" : undefined,
                    px: 2,
                }}
            >
                <Typography>Loading puzzle…</Typography>
            </Box>
        );
        if (contentRecordingMode) {
            return (
                <RecordingCaptureStage stageBg={tokens.pageBg}>
                    {loadingBody}
                </RecordingCaptureStage>
            );
        }
        return loadingBody;
    }

    if (snapshotMode && effectiveSlidingId && !snapshotPuzzleLoaded && !localBoardReady) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "50dvh",
                    px: 2,
                }}
            >
                <Typography sx={{ color: tokens.textMuted }}>
                    {snapshotLoadFailed ? "Could not load puzzle for screenshot." : "Loading puzzle…"}
                </Typography>
            </Box>
        );
    }

    const playNav = (
        <PlayPageNav
            title={displayTitle}
            onBack={() => navigate(playReturnPath)}
            originalImageUrl={null}
            previewBoardSize={previewBoardSize}
            overlayBoardRect={boardOverlayRect}
            getOverlayBoardRect={getBoardOverlayRect}
            backAriaLabel={t("native.sliding.play.back", { defaultValue: "Back" })}
            viewOriginalAriaLabel={t("puzzles-browse.sliding-play-view-original-image", {
                defaultValue: "View original image",
            })}
            compact={isLandscape}
            showTitle={!isLandscape}
        />
    );

    const toolbarActions = (
        <>
            {bgmSettingsLoaded ? (
                <AudioButton
                    key={bgmAutoPlay ? "bgm-on" : "bgm-off"}
                    active={!snapshotMode}
                    localSrc={LOCAL_BGM_DEEP_URBAN}
                    defaultPlaying={bgmAutoPlay}
                    cloudUserId={cloudUserId}
                    persistPreference={false}
                    onPlayingChange={handleBgmPlayingChange}
                    size="medium"
                    sx={{
                        ...toolBtnSx,
                        color: tokens.text,
                    }}
                />
            ) : null}
            {!snapshotMode &&
            !puzzleSolved &&
            !solveInProgress &&
            !sessionStartsSolved ? (
                <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
                    <GhostGuideHintButton
                        pointBalance={hintPoints.pointBalance}
                        disabled={
                            contentRecordingMode
                                ? contentAutoSolveBusy && !recordingGhostActive
                                : ghostGuide.buttonDisabled || contentAutoSolveBusy
                        }
                        active={
                            contentRecordingMode
                                ? recordingGhostActive
                                : ghostGuide.ghostGuideActive
                        }
                        glass={playGlass}
                        size={toolbarBtnSize}
                        iconSize={toolbarIconSize}
                        onActivate={() => {
                            if (
                                !GHOST_GUIDE_TEST_MODE &&
                                hintPoints.pointBalance < HINT_POINT_COST
                            ) {
                                setHintPointsSheetOpen(true);
                                return;
                            }
                            void ghostGuide.activateGhostGuide();
                        }}
                    />
                    {contentSixBySixHard && recordingGhostActive ? (
                        <Box
                            component="span"
                            role="status"
                            sx={{
                                position: "absolute",
                                left: "calc(100% + 12px)",
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 6,
                                px: 1.75,
                                py: 0.85,
                                borderRadius: "999px",
                                bgcolor: "rgba(15, 23, 42, 0.92)",
                                color: "#ecfeff",
                                border: "2px solid rgba(0, 255, 200, 0.95)",
                                boxShadow:
                                    "0 0 16px rgba(0, 255, 200, 0.45), 0 2px 10px rgba(0,0,0,0.35)",
                                fontFamily:
                                    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                                fontWeight: 800,
                                fontSize: contentRecordingMode ? "1.2rem" : "0.95rem",
                                letterSpacing: "0.01em",
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                            }}
                        >
                            Ghost guide
                        </Box>
                    ) : null}
                </Box>
            ) : null}
            {originalImageUrl ? (
                <IconImagePreview
                    imageUrl={originalImageUrl}
                    tooltip={t("puzzles-browse.sliding-play-view-original-image", {
                        defaultValue: "View original image",
                    })}
                    hideTitle
                    centeredOverlay
                    overlayBoardSize={previewBoardSize}
                    overlayBoardRect={boardOverlayRect}
                    getOverlayBoardRect={getBoardOverlayRect}
                    sx={toolBtnSx}
                />
            ) : null}
            {showTileModeToggle ? (
                <IconButton
                    type="button"
                    aria-label={
                        tileMode === "image"
                            ? t("native.sliding.play.showNumbers", {
                                  defaultValue: "Show numbers",
                              })
                            : t("native.sliding.play.showImage", {
                                  defaultValue: "Show image",
                              })
                    }
                    onClick={() =>
                        setTileMode((mode) => {
                            const next = mode === "image" ? "numbers" : "image";
                            savePlayTileModePreference(next);
                            return next;
                        })
                    }
                    sx={toolBtnSx}
                >
                    {tileMode === "image" ? (
                        <TagRoundedIcon sx={toolIconSx} />
                    ) : (
                        <ImageOutlinedIcon sx={toolIconSx} />
                    )}
                </IconButton>
            ) : null}
            {!snapshotMode ? (
                <IconButton
                    type="button"
                    aria-label={t("native.settings.screenTitle", {
                        defaultValue: "Settings",
                    })}
                    onClick={() => {
                        const playState = {
                            ...state,
                            slidingId:
                                effectiveSlidingId ||
                                (typeof state.slidingId === "string"
                                    ? state.slidingId.trim()
                                    : "") ||
                                undefined,
                            gridSize,
                            difficulty,
                            imageUrl,
                            storyTitle: displayTitle,
                            previewImageUrl: originalImageUrl || state.previewImageUrl,
                            layout: Array.isArray(liveLayoutRef.current)
                                ? liveLayoutRef.current.map((v) => Number(v))
                                : state.layout,
                            resume: true,
                            returnPath: playReturnPath,
                        };
                        savePlayRouteState(playState);
                        navigate(SETTINGS_PATH, {
                            state: {
                                returnToPlay: true,
                                playState,
                            },
                        });
                    }}
                    sx={toolBtnSx}
                >
                    <SettingsIcon sx={toolIconSx} />
                </IconButton>
            ) : null}
        </>
    );

    const playShell = (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                alignSelf: "stretch",
                minHeight: 0,
                width: "100%",
                height: contentRecordingMode ? "100%" : undefined,
                boxSizing: "border-box",
                position: "relative",
                isolation: "isolate",
                color: tokens.text,
            }}
        >
            <SlidingPlayMistBackground />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: isLandscape ? "row" : "column",
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    height: contentRecordingMode ? "100%" : undefined,
                    px: 1,
                    ...(isLandscape
                        ? {
                              pl: PLAY_LANDSCAPE_LEFT_PAD,
                              pr: PLAY_LANDSCAPE_RIGHT_PAD,
                              pt: PLAY_LANDSCAPE_TOP_PAD,
                              pb: PLAY_LANDSCAPE_BOTTOM_PAD,
                          }
                        : { pt: 0, pb: 0 }),
                    boxSizing: "border-box",
                }}
            >
            {snapshotMode && snapshotPuzzleLoaded ? (
                <Typography
                    component="span"
                    aria-label={SNAPSHOT_PLAY_READY_LABEL}
                    sx={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        overflow: "hidden",
                        clip: "rect(0,0,0,0)",
                        whiteSpace: "nowrap",
                    }}
                >
                    {SNAPSHOT_PLAY_READY_LABEL}
                </Typography>
            ) : null}
            {isLandscape ? null : playNav}

            <PlaySessionHud
                orientation={isLandscape ? "vertical" : "horizontal"}
                leading={isLandscape ? playNav : null}
                showTimer={showPlayTimer}
                timeLabel={playTimer.formatted}
                moveCount={displayMoves}
                modeLabel={
                    tileMode === "numbers"
                        ? t("native.sliding.play.modeNumbers", { defaultValue: "Numbers" })
                        : t("native.sliding.play.modeImage", { defaultValue: "Image" })
                }
                paused={playTimer.paused}
                manualPaused={playTimer.manualPaused}
                onTogglePause={
                    showPlayTimer &&
                    apiReady &&
                    !puzzleSolved &&
                    !solveInProgress &&
                    !contentAutoSolveBusy
                        ? playTimer.toggleManualPause
                        : null
                }
                snapshotMarker={snapshotMode && snapshotPuzzleLoaded}
                large={contentRecordingMode}
                actions={toolbarActions}
            />

            <Box sx={playBoardSlotSx} aria-label="Sliding puzzle board" role="img">
                <Box
                    ref={boardRef}
                    sx={{
                        ...playBoardSquareSx,
                    }}
                >
                    <SlidingPlayMain
                        key={`${effectiveSlidingId || "local"}-${gridSize}-${difficulty}-${layoutKey}-${useSavedLayout ? "s" : "n"}`}
                        imageUrl={imageUrl}
                        revealImageUrl={revealImageUrl}
                        gridSize={gridSize}
                        startingLayout={boardLayout}
                        playMoveSound={playSoundEffects}
                        playLineMatchSound={playSoundEffects}
                        hideCompletedCaption
                        fillParent
                        {...slidingPlayGridProps(tokens)}
                        tileMode={tileMode}
                        numbersDark={isDark}
                        onLayoutChange={handleLayoutChange}
                        onMove={handleMove}
                        lineMatchCelebration
                        lineMatchLabel={t("native.sliding.play.lineMatchGoodJob", {
                            defaultValue: "Good Job!",
                        })}
                        ghostGuideMarks={
                            contentRecordingMode
                                ? recordingGhostMarks
                                : ghostGuide.ghostGuideMarks
                        }
                        boardInputLocked={
                            ghostGuide.boardInputLocked || contentAutoSolveBusy
                        }
                        animateSolveReveal={!sessionStartsSolved}
                        startWithRevealImage={Boolean(revealImageUrl) && sessionStartsSolved}
                        onSolvedRevealComplete={handleSolveRevealComplete}
                    />
                </Box>
            </Box>

            {showPlayHint && !isLandscape ? (
            <Typography
                component="p"
                sx={{
                    flexShrink: 0,
                    textAlign: "center",
                    fontSize: "0.875rem",
                    lineHeight: 1.45,
                    color: tokens.textMuted,
                    py: 1,
                    pb: `calc(8px + ${slidingSafeAreaBottom})`,
                    px: 0.5,
                    fontFamily:
                        '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
                }}
            >
                {t("native.sliding.play.hint", {
                    defaultValue: "Tap any tile beside the gray cell to move it.",
                })}
            </Typography>
            ) : null}

            <SlidingPlayConfetti active={confettiActive} burstKey={confettiBurstKey} />

            <SlidingVictoryModal
                open={successCardOpen && !contentRecordingMode}
                onClose={() => setSuccessCardOpen(false)}
                stats={successCardStats}
                showTime={false}
                onPlayAgain={handleVictoryPlayAgain}
                onNextPuzzle={
                    nextSameConfigPuzzle?.slidingId ? handleVictoryNextPuzzle : undefined
                }
                nextPuzzleDisabled={nextPuzzleStarting}
                onSignUp={
                    showVictorySignUp
                        ? () => {
                              setSuccessCardOpen(false);
                              navigate(NATIVE_PURCHASE_PATH, {
                                  state: { productSku: SLIDING_MASTER_PRODUCT_SKU },
                              });
                          }
                        : undefined
                }
                signUpLabel={t("login.sign-up", { defaultValue: "Sign up" })}
            />

            <HintPointsSheet
                open={hintPointsSheetOpen}
                onClose={() => setHintPointsSheetOpen(false)}
                pointBalance={hintPoints.pointBalance}
                packAmount={hintPoints.packAmount}
                packPrice={hintPoints.getPackPrice()}
                packagesLoading={hintPoints.packagesLoading}
                isProcessing={hintPoints.isProcessing}
                isIapAvailable={hintPoints.isIapAvailable}
                onPurchase={purchaseHintPoints}
            />
            </Box>
        </Box>
    );

    if (contentRecordingMode) {
        return (
            <RecordingCaptureStage stageBg={tokens.pageBg}>{playShell}</RecordingCaptureStage>
        );
    }
    return playShell;
}
